import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { downloadReport } from "@/lib/api";
import { inr, fmtDate, today, billNo } from "@/lib/format";
import { PageTitle } from "@/components/PageTitle";
import { DatePicker } from "@/components/DatePicker";
import { SearchSelect } from "@/components/SearchSelect";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ShoppingCart, Wallet, Receipt, FileDown, FileSpreadsheet, CalendarDays } from "lucide-react";

export default function DayLedger() {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [supplierId, setSupplierId] = useState("all");
  const [showP, setShowP] = useState(true);
  const [showPay, setShowPay] = useState(true);
  const [showE, setShowE] = useState(true);

  const { data: suppliers = [] } = useQuery({ queryKey: ["suppliers-min"], queryFn: async () => (await api.get("/suppliers")).data });

  const { data } = useQuery({
    queryKey: ["ledger", from, to, supplierId, showP, showPay, showE],
    queryFn: async () => (await api.get("/ledger", {
      params: {
        date_from: from || undefined, date_to: to || undefined,
        supplier_id: supplierId === "all" ? undefined : supplierId,
        show_purchases: showP, show_payments: showPay, show_expenses: showE,
      },
    })).data,
  });

  const s = data?.summary || {};
  const purchases = data?.purchases || [];
  const payments = data?.payments || [];
  const expenses = data?.expenses || [];

  return (
    <div>
      <PageTitle title="Day Ledger" subtitle="Purchases, payments & expenses in one view">
        <Button onClick={() => downloadReport("/ledger/export", { fmt: "pdf", date_from: today(), date_to: today(), show_purchases: true, show_payments: true, show_expenses: true }, "today_summary")} data-testid="ledger-today">
          <CalendarDays className="h-4 w-4" /> Today's Summary
        </Button>
        <Button variant="outline" onClick={() => downloadReport("/ledger/export", { fmt: "pdf", date_from: from, date_to: to, supplier_id: supplierId === "all" ? undefined : supplierId, show_purchases: showP, show_payments: showPay, show_expenses: showE }, "day_ledger")} data-testid="ledger-pdf">
          <FileDown className="h-4 w-4" /> PDF
        </Button>
        <Button variant="outline" onClick={() => downloadReport("/ledger/export", { fmt: "excel", date_from: from, date_to: to, supplier_id: supplierId === "all" ? undefined : supplierId, show_purchases: showP, show_payments: showPay, show_expenses: showE }, "day_ledger")} data-testid="ledger-excel">
          <FileSpreadsheet className="h-4 w-4" /> Excel
        </Button>
      </PageTitle>

      <Card className="p-4 mb-4">
        <div className="grid md:grid-cols-4 gap-3">
          <div><Label className="text-xs">From</Label><div className="mt-1"><DatePicker value={from} onChange={setFrom} testid="ledger-from" /></div></div>
          <div><Label className="text-xs">To</Label><div className="mt-1"><DatePicker value={to} onChange={setTo} testid="ledger-to" /></div></div>
          <div className="md:col-span-2">
            <Label className="text-xs">Supplier</Label>
            <div className="mt-1">
              <SearchSelect
                value={supplierId}
                onChange={setSupplierId}
                options={[{ value: "all", label: "All Suppliers" }, ...suppliers.map((sp) => ({ value: sp.id, label: `${sp.supplier_code} — ${sp.name}` }))]}
                searchPlaceholder="Type supplier name or code…"
                testid="ledger-supplier"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 mt-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={showP} onCheckedChange={(v) => setShowP(!!v)} data-testid="toggle-purchases" /> Purchases</label>
          <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={showPay} onCheckedChange={(v) => setShowPay(!!v)} data-testid="toggle-payments" /> Payments</label>
          <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={showE} onCheckedChange={(v) => setShowE(!!v)} data-testid="toggle-expenses" /> Expenses</label>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <Card className="p-4 flex items-center gap-3"><div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><ShoppingCart className="h-5 w-5" /></div><div><div className="text-xs text-muted-foreground">Purchases ({s.purchase_count ?? 0})</div><div className="font-bold data-num">{inr(s.total_purchase)}</div></div></Card>
        <Card className="p-4 flex items-center gap-3"><div className="grid h-10 w-10 place-items-center rounded-lg bg-success/10 text-success"><Wallet className="h-5 w-5" /></div><div><div className="text-xs text-muted-foreground">Payments ({s.payment_count ?? 0})</div><div className="font-bold data-num">{inr(s.total_payment)}</div></div></Card>
        <Card className="p-4 flex items-center gap-3"><div className="grid h-10 w-10 place-items-center rounded-lg bg-accent/20 text-accent-foreground"><Receipt className="h-5 w-5" /></div><div><div className="text-xs text-muted-foreground">Expenses ({s.expense_count ?? 0})</div><div className="font-bold data-num">{inr(s.total_expense)}</div></div></Card>
      </div>

      {showP && (
        <Card className="overflow-hidden mb-4">
          <div className="p-3 border-b bg-muted/30 font-semibold text-sm flex items-center gap-2"><ShoppingCart className="h-4 w-4 text-primary" /> Purchases</div>
          <div className="overflow-x-auto ssacc-scroll">
            <Table>
              <TableHeader><TableRow><TableHead>Bill No</TableHead><TableHead>Date</TableHead><TableHead>Supplier</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
              <TableBody>
                {purchases.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">None</TableCell></TableRow> : purchases.map((p) => (
                  <TableRow key={p.id}><TableCell className="font-medium text-primary">{billNo(p.bill_number)}</TableCell><TableCell>{fmtDate(p.date)}</TableCell><TableCell>{p.supplier_name}</TableCell><TableCell className="text-right data-num">{inr(p.total)}</TableCell><TableCell className="text-right data-num">{inr(p.balance)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {showPay && (
        <Card className="overflow-hidden mb-4">
          <div className="p-3 border-b bg-muted/30 font-semibold text-sm flex items-center gap-2"><Wallet className="h-4 w-4 text-success" /> Payments</div>
          <div className="overflow-x-auto ssacc-scroll">
            <Table>
              <TableHeader><TableRow><TableHead>Payment No</TableHead><TableHead>Date</TableHead><TableHead>Supplier</TableHead><TableHead>Bills</TableHead><TableHead className="text-right">Net Paid</TableHead></TableRow></TableHeader>
              <TableBody>
                {payments.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">None</TableCell></TableRow> : payments.map((p) => (
                  <TableRow key={p.id}><TableCell className="font-medium text-primary">{p.payment_no}</TableCell><TableCell>{fmtDate(p.date)}</TableCell><TableCell>{p.supplier_name}</TableCell><TableCell className="text-xs">{p.allocations?.map((a) => billNo(a.bill_number)).join(", ")}</TableCell><TableCell className="text-right data-num">{inr(p.net_amount)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {showE && (
        <Card className="overflow-hidden">
          <div className="p-3 border-b bg-muted/30 font-semibold text-sm flex items-center gap-2"><Receipt className="h-4 w-4 text-accent-foreground" /> Expenses</div>
          <div className="overflow-x-auto ssacc-scroll">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Memo</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
              <TableBody>
                {expenses.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">None</TableCell></TableRow> : expenses.map((e) => (
                  <TableRow key={e.id}><TableCell>{fmtDate(e.date)}</TableCell><TableCell className="font-medium">{e.name}</TableCell><TableCell className="text-xs text-muted-foreground">{e.memo || "—"}</TableCell><TableCell className="text-right data-num">{inr(e.amount)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
